import { useState, useEffect, useMemo, Component } from 'react';
import { structureRebuiltSourceLabel } from './chat/StructureRebuiltNotice';
import type { ReactNode, ErrorInfo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE } from '../lib/api';
import AnalysisReportView from './AnalysisReportView';
import ConfidenceBar from './ConfidenceBar';
import QAReportView, { isQAReport } from './QAReportView';
import CodeReviewReportCard, { type CodeReviewReport } from './agents/CodeReviewReportCard';
import MarkdownRenderer from './MarkdownRenderer';
import PrdFrEditor from './PrdFrEditor';
import ScreenPreviewCard from './ScreenPreviewCard';
import PrototypeViewer from './PrototypeViewer';
import ArtefactSectionFeedback from './ArtefactSectionFeedback';
import TokenReimportModal from './TokenReimportModal';
import ArtefactProvenanceLine from './audit/ArtefactProvenanceLine';
import ClaudeDesignTour, { CLAUDE_DESIGN_TOUR_FEATURE } from './ClaudeDesignTour';
import FilingBadge from './drive/FilingBadge';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import { getEffectiveClaudeDesignExportEnabled } from '@blueprint/shared';
import { downloadClaudeDesignZip, fetchClaudeDesignPrompt } from '../lib/claudeDesignExport';
import type { AnalysisReport } from '../types/analysis';
import type { QAReport } from '../types/qa-report';

interface ArtefactDetail {
  id: string;
  projectId: string;
  type: string;
  title: string;
  currentVersion: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ConfidenceMetadata {
  overall: number;
  sections: Record<string, number>;
  lowConfidenceCount: number;
  flaggedForReview: string[];
}

interface VersionDetail {
  id: string;
  version: string;
  notes: string | null;
  changeSummary: string | null;
  createdAt: string;
  confidenceScore: string | null;
  confidenceMetadata: ConfidenceMetadata | null;
  status?: string | null;
}

export interface ArtefactMeta {
  title: string;
  type: string;
  status: string;
}

interface Props {
  artefactId: string;
  onMetadataLoaded?: (meta: ArtefactMeta) => void;
}

interface StalenessFlag {
  id: string;
  reason: string;
  amendmentId: string;
  createdAt: string;
}

const MARKDOWN_TYPES = ['prd', 'user_stories', 'sad', 'adr', 'sprint_plan'];

// Types that can be approved from the artefact detail panel when the
// version is in `draft` status. The Phase 2.5 UX types were missing
// from this list, so Brazil Payroll Calculator's three recovered
// drafts (screen_inventory / screen_spec / component_catalogue) had
// no Approve button on the artefacts page even though the backend
// PATCH /artefacts/:id/status accepted draft → approved for them.
// Kept separate from MARKDOWN_TYPES so the existing markdown-preview
// path (which uses the {markdown} envelope shape) isn't disturbed by
// types that store a {markdown, json} envelope.
const APPROVABLE_DRAFT_TYPES = [
  ...MARKDOWN_TYPES,
  'sprint_backlog',
  'qa_report',
  'code_review_report',
  'user_journey_map',
  'screen_inventory',
  'screen_spec',
  'component_catalogue',
  'design_tokens',
  'claude_design_bundle',
];

// DISC.A3 (#1475) — the five Phase 2.5 (UX Design) artefact types. ALL must be
// `approved` before the Claude Design export / Copy Prompt actions unlock.
// Defined inline pending sibling story B1, which promotes this to
// PHASE_25_ARTEFACT_TYPES in apps/web/src/lib/constants.ts; collapse to that
// import once B1 lands (dedup tracked there).
const PHASE_25_TYPES = [
  'user_journey_map',
  'screen_inventory',
  'screen_spec',
  'component_catalogue',
  'design_tokens',
] as const;

interface BacklogStory { id?: string; title?: string; points?: number; priority?: string; epic?: string; component?: string; assignee?: string; dependencies?: string[]; status?: string; }
interface BacklogSpike { id?: string; title?: string; timeBox?: string; component?: string; assignee?: string; status?: string; }
interface BacklogInfraTask { id?: string; title?: string; effort?: string; blocks?: string[]; assignee?: string; }
interface BacklogDeferred { id?: string; title?: string; points?: number; priority?: string; reasonDeferred?: string; targetSprint?: string; }
interface BacklogBuildItem { order?: number; id?: string; title?: string; unlocks?: string[]; developer?: string; }
interface BacklogRisk { id?: string; description?: string; severity?: string; affectedItems?: string[]; mitigation?: string; }
interface BacklogPreSprintAction { action?: string; owner?: string; requiredBy?: string; status?: string; }
interface SprintBacklogJson {
  sprint?: { name?: string; goal?: string; duration?: string; velocityTarget?: number; team?: Array<{ name?: string; role?: string; capacity?: number }>; };
  stories?: BacklogStory[];
  spikes?: BacklogSpike[];
  infraTasks?: BacklogInfraTask[];
  deferred?: BacklogDeferred[];
  buildOrder?: BacklogBuildItem[];
  risks?: BacklogRisk[];
  preSprintActions?: BacklogPreSprintAction[];
  summary?: { totalStories?: number; totalSpikes?: number; totalInfraTasks?: number; totalPoints?: number; storiesByPriority?: Record<string, number>; storiesByComponent?: Record<string, number>; criticalPathLength?: number; highRisks?: number; };
}

function isSprintBacklog(notes: string): SprintBacklogJson | null {
  try {
    const parsed = JSON.parse(notes) as { json?: SprintBacklogJson };
    if (parsed.json && typeof parsed.json === 'object' && 'stories' in parsed.json) return parsed.json;
  } catch { /* fall through */ }
  return null;
}

function isAnalysisReport(value: unknown): value is AnalysisReport {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.summary === 'string' &&
    Array.isArray(v.gaps) &&
    Array.isArray(v.risks) &&
    Array.isArray(v.openQuestions) &&
    Array.isArray(v.inconsistencies) &&
    Array.isArray(v.nextActions)
  );
}

interface BARInventoryItem { artefactTitle?: string; artefactType?: string; status?: string; completenessPercent?: number; keyGaps?: string; }
interface BARPhaseGate { phase?: string; status?: string; entryGateMet?: boolean; blockers?: string[]; }
interface BARRisk { description?: string; severity?: string; source?: string; category?: string; }
interface BAREntryPoint { phase?: string; rationale?: string; prerequisites?: string[]; }
interface BAREffortItem { phase?: string; estimate?: string; confidence?: string; notes?: string; }
interface BrownfieldAssessment {
  executiveSummary: string;
  readinessScore: number;
  artefactInventory?: BARInventoryItem[];
  phaseGateReadiness?: BARPhaseGate[];
  riskRegister?: BARRisk[];
  recommendedEntryPoint?: BAREntryPoint;
  effortEstimate?: BAREffortItem[];
}

function isBrownfieldAssessment(value: unknown): value is BrownfieldAssessment {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.executiveSummary === 'string' && typeof v.readinessScore === 'number';
}

const thStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', marginTop: 8 };
const sectionHeadStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, marginTop: 20 };
const priorityColor = (p: string | undefined) => p === 'P0' ? 'var(--error)' : p === 'P1' ? 'var(--warning)' : 'var(--text-secondary)';

function SprintBacklogView({ backlog }: { backlog: SprintBacklogJson }) {
  const s = backlog.sprint;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Sprint metadata */}
      {s && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 20 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{s.name ?? 'Sprint Backlog'}</p>
          {s.goal && <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>{s.goal}</p>}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {s.duration && <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Duration: <strong style={{ color: 'var(--text)' }}>{s.duration}</strong></span>}
            {s.velocityTarget !== undefined && <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Velocity Target: <strong style={{ color: 'var(--text)' }}>{s.velocityTarget} pts</strong></span>}
            {s.team?.length ? <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Team: <strong style={{ color: 'var(--text)' }}>{s.team.map(t => t.name).join(', ')}</strong></span> : null}
          </div>
        </div>
      )}

      {/* Summary stats */}
      {backlog.summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Stories', value: backlog.summary.totalStories },
            { label: 'Spikes', value: backlog.summary.totalSpikes },
            { label: 'Infra Tasks', value: backlog.summary.totalInfraTasks },
            { label: 'Total Points', value: backlog.summary.totalPoints },
            { label: 'Critical Path', value: backlog.summary.criticalPathLength !== undefined ? `${backlog.summary.criticalPathLength} items` : undefined },
            { label: 'High Risks', value: backlog.summary.highRisks },
          ].filter(x => x.value !== undefined).map(({ label, value }) => (
            <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>{value}</p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Stories */}
      {!!backlog.stories?.length && (
        <>
          <p style={sectionHeadStyle}>Stories ({backlog.stories.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>ID</th><th style={thStyle}>Title</th><th style={thStyle}>Pts</th>
                <th style={thStyle}>Priority</th><th style={thStyle}>Component</th><th style={thStyle}>Assignee</th><th style={thStyle}>Status</th>
              </tr></thead>
              <tbody>{backlog.stories.map((s, i) => (
                <tr key={s.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.id}</td>
                  <td style={tdStyle}>{s.title}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{s.points}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: priorityColor(s.priority) }}>{s.priority}</td>
                  <td style={tdStyle}>{s.component}</td>
                  <td style={tdStyle}>{s.assignee ?? 'TBD'}</td>
                  <td style={tdStyle}>{s.status}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Spikes */}
      {!!backlog.spikes?.length && (
        <>
          <p style={sectionHeadStyle}>Spikes ({backlog.spikes.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>ID</th><th style={thStyle}>Title</th><th style={thStyle}>Time-box</th><th style={thStyle}>Component</th><th style={thStyle}>Assignee</th>
              </tr></thead>
              <tbody>{backlog.spikes.map((s, i) => (
                <tr key={s.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.id}</td>
                  <td style={tdStyle}>{s.title}</td>
                  <td style={tdStyle}>{s.timeBox}</td>
                  <td style={tdStyle}>{s.component}</td>
                  <td style={tdStyle}>{s.assignee ?? 'TBD'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Infra tasks */}
      {!!backlog.infraTasks?.length && (
        <>
          <p style={sectionHeadStyle}>Infrastructure Tasks ({backlog.infraTasks.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>ID</th><th style={thStyle}>Title</th><th style={thStyle}>Effort</th><th style={thStyle}>Blocks</th><th style={thStyle}>Assignee</th>
              </tr></thead>
              <tbody>{backlog.infraTasks.map((t, i) => (
                <tr key={t.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{t.id}</td>
                  <td style={tdStyle}>{t.title}</td>
                  <td style={tdStyle}>{t.effort}</td>
                  <td style={tdStyle}>{Array.isArray(t.blocks) ? t.blocks.join(', ') : (t.blocks || '')}</td>
                  <td style={tdStyle}>{t.assignee ?? 'TBD'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Build order */}
      {!!backlog.buildOrder?.length && (
        <>
          <p style={sectionHeadStyle}>Build Order</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>#</th><th style={thStyle}>ID</th><th style={thStyle}>Title</th><th style={thStyle}>Unlocks</th><th style={thStyle}>Developer</th>
              </tr></thead>
              <tbody>{backlog.buildOrder.map((b, i) => (
                <tr key={b.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center' }}>{b.order}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{b.id}</td>
                  <td style={tdStyle}>{b.title}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{Array.isArray(b.unlocks) ? b.unlocks.join(', ') : (b.unlocks || '')}</td>
                  <td style={tdStyle}>{b.developer}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Deferred */}
      {!!backlog.deferred?.length && (
        <>
          <p style={sectionHeadStyle}>Deferred ({backlog.deferred.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>ID</th><th style={thStyle}>Title</th><th style={thStyle}>Pts</th><th style={thStyle}>Reason</th><th style={thStyle}>Target Sprint</th>
              </tr></thead>
              <tbody>{backlog.deferred.map((d, i) => (
                <tr key={d.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{d.id}</td>
                  <td style={tdStyle}>{d.title}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{d.points}</td>
                  <td style={tdStyle}>{d.reasonDeferred}</td>
                  <td style={tdStyle}>{d.targetSprint}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Risk register */}
      {!!backlog.risks?.length && (
        <>
          <p style={sectionHeadStyle}>Risk Register ({backlog.risks.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>ID</th><th style={thStyle}>Description</th><th style={thStyle}>Severity</th><th style={thStyle}>Affected</th><th style={thStyle}>Mitigation</th>
              </tr></thead>
              <tbody>{backlog.risks.map((r, i) => (
                <tr key={r.id ?? i}>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.id}</td>
                  <td style={tdStyle}>{r.description}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: r.severity === 'High' ? 'var(--error)' : r.severity === 'Medium' ? 'var(--warning)' : 'var(--text-secondary)' }}>{r.severity}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{Array.isArray(r.affectedItems) ? r.affectedItems.join(', ') : (r.affectedItems || '')}</td>
                  <td style={tdStyle}>{r.mitigation}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Pre-sprint actions */}
      {!!backlog.preSprintActions?.length && (
        <>
          <p style={sectionHeadStyle}>Pre-Sprint Actions ({backlog.preSprintActions.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 16 }}>
            {backlog.preSprintActions.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <span style={{ marginTop: 2, flexShrink: 0 }}>☐</span>
                <div>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>{a.action}</span>
                  {(a.owner || a.requiredBy) && (
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                      {a.owner ? `Owner: ${a.owner}` : ''}{a.owner && a.requiredBy ? ' · ' : ''}{a.requiredBy ? `By: ${a.requiredBy}` : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const severityColor = (s: string | undefined) => {
  const up = (s ?? '').toUpperCase();
  if (up === 'HIGH' || up === 'CRITICAL') return 'var(--error)';
  if (up === 'MEDIUM') return 'var(--warning)';
  return 'var(--text-secondary)';
};

const readinessColor = (score: number) => score >= 70 ? 'var(--success)' : score >= 40 ? 'var(--warning)' : 'var(--error)';

class RenderErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  constructor(props: { fallback: ReactNode; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('[RenderErrorBoundary]', error, info); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

function BrownfieldAssessmentView({ report }: { report: BrownfieldAssessment }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Readiness Score */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', marginBottom: 20, textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Overall Readiness Score</p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 48, fontWeight: 700, color: readinessColor(report.readinessScore), lineHeight: 1 }}>{report.readinessScore}<span style={{ fontSize: 20, color: 'var(--text-secondary)' }}>/100</span></p>
      </div>

      {/* Executive Summary */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 20 }}>
        <p style={sectionHeadStyle}>Executive Summary</p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>{report.executiveSummary}</p>
      </div>

      {/* Artefact Inventory */}
      {Array.isArray(report.artefactInventory) && report.artefactInventory.length > 0 && (
        <>
          <p style={sectionHeadStyle}>Artefact Inventory ({report.artefactInventory.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Title</th><th style={thStyle}>Type</th><th style={thStyle}>Status</th><th style={thStyle}>Completeness</th><th style={thStyle}>Key Gaps</th>
              </tr></thead>
              <tbody>{report.artefactInventory.map((a, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{a.artefactTitle}</td>
                  <td style={tdStyle}>{a.artefactType}</td>
                  <td style={tdStyle}>{a.status}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{a.completenessPercent ?? 0}%</td>
                  <td style={tdStyle}>{a.keyGaps}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Phase Gate Readiness */}
      {Array.isArray(report.phaseGateReadiness) && report.phaseGateReadiness.length > 0 && (
        <>
          <p style={sectionHeadStyle}>Phase Gate Readiness ({report.phaseGateReadiness.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Phase</th><th style={thStyle}>Status</th><th style={thStyle}>Blockers</th>
              </tr></thead>
              <tbody>{report.phaseGateReadiness.map((p, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{p.phase}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: p.entryGateMet ? 'var(--success)' : p.status === 'Partial' ? 'var(--warning)' : 'var(--error)' }}>{p.status ?? (p.entryGateMet ? 'Met' : 'Blocked')}</td>
                  <td style={tdStyle}>{Array.isArray(p.blockers) ? p.blockers.join(', ') : (p.blockers || '—')}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Risk Register */}
      {Array.isArray(report.riskRegister) && report.riskRegister.length > 0 && (
        <>
          <p style={sectionHeadStyle}>Risk & Debt Register ({report.riskRegister.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Description</th><th style={thStyle}>Severity</th><th style={thStyle}>Source</th><th style={thStyle}>Category</th>
              </tr></thead>
              <tbody>{report.riskRegister.map((r, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{r.description}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: severityColor(r.severity) }}>{r.severity}</td>
                  <td style={tdStyle}>{r.source}</td>
                  <td style={tdStyle}>{r.category}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {/* Recommended Entry Point */}
      {report.recommendedEntryPoint && (
        <>
          <p style={sectionHeadStyle}>Recommended Entry Point</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 16 }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>{report.recommendedEntryPoint.phase ?? 'N/A'}</p>
            {report.recommendedEntryPoint.rationale && <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>{report.recommendedEntryPoint.rationale}</p>}
            {(() => {
              const prereqs = report.recommendedEntryPoint!.prerequisites;
              const items = Array.isArray(prereqs) ? prereqs : (typeof prereqs === 'string' && prereqs ? [prereqs] : []);
              return items.length > 0 ? (
                <div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Prerequisites:</p>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {items.map((p, i) => (
                      <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{p}</li>
                    ))}
                  </ul>
                </div>
              ) : null;
            })()}
          </div>
        </>
      )}

      {/* Effort Estimate */}
      {Array.isArray(report.effortEstimate) && report.effortEstimate.length > 0 && (
        <>
          <p style={sectionHeadStyle}>Effort Estimate ({report.effortEstimate.length})</p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto', marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Phase</th><th style={thStyle}>Estimate</th><th style={thStyle}>Confidence</th><th style={thStyle}>Notes</th>
              </tr></thead>
              <tbody>{report.effortEstimate.map((e, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{e.phase}</td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{e.estimate}</td>
                  <td style={tdStyle}>{e.confidence}</td>
                  <td style={tdStyle}>{e.notes}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const barRenderFallback = (
  <div style={{ padding: 24, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)' }}>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--error)', marginBottom: 8 }}>Unable to render Brownfield Assessment Report</p>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>The report data contains unexpected field types. Try regenerating the assessment.</p>
  </div>
);

const crRenderFallback = (
  <div style={{ padding: 24, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)' }}>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--error)', marginBottom: 8 }}>Unable to render Code Review Report</p>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>The report data contains unexpected field types. Try regenerating the review.</p>
  </div>
);

export default function ArtefactDetailContent({ artefactId, onMetadataLoaded }: Props) {
  const navigate = useNavigate();
  // Sprint 12.5 ACC.B6 — read the tenant's quality_feedback_enabled
  // flag once per mount. The hook fetches /tenants/me/membership
  // (which now surfaces the flag); zero marginal cost if another
  // component already mounted it.
  const { myTenant } = useMyTenant();
  const [artefact, setArtefact] = useState<ArtefactDetail | null>(null);
  const [versions, setVersions] = useState<VersionDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [exportError, setExportError] = useState('');
  // #3488 — diagrams the .docx could only include as source (X-Docx-Diagrams-Failed,
  // and delta D3's X-Docx-Diagrams-Skipped: over the per-document cap or refused
  // by a busy diagram service — not a failure; X-Docx-Diagrams-Busy says how many
  // were busy refusals, the only skipped ones a re-export may help).
  // Tagged with the artefact it is about, so it is not shown once the page
  // moves to another artefact (operator pin; no setState-in-effect reset).
  const [exportNotice, setExportNotice] = useState<{ artefactId: string; text: string } | null>(null);
  // Sprint 11 UX.F2 — Claude Design export.
  const [claudeExporting, setClaudeExporting] = useState(false);
  const [claudeCopied, setClaudeCopied] = useState(false);
  // DISC.C1 (#1478) — Claude Design round-trip guided tour. featureAck holds the
  // caller's server-side acknowledgement map; featureAckLoaded flips true once the
  // read settles (so a click that lands before the read resolves waits for it,
  // and an already-dismissed user is never re-nagged on a fast click — R2).
  // tourTriggered flips true the first time the user clicks Export / Copy Prompt.
  const [featureAck, setFeatureAck] = useState<Record<string, boolean> | null>(null);
  const [featureAckLoaded, setFeatureAckLoaded] = useState(false);
  const [tourTriggered, setTourTriggered] = useState(false);
  // UXV.F3 (#1424) — "Import token refinements" round-trip.
  const [showReimport, setShowReimport] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  // SL.D1 (#2951, 6406880252 item 3): revoke a sprint plan's Phase 3 approval (Q2's 409 points here).
  const [projectPhase, setProjectPhase] = useState<string | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');
  // Phase 2.5 artefacts (user_journey_map, screen_inventory, screen_spec,
  // component_catalogue, design_tokens) store notes as `{ markdown, json }`.
  // The Preview tab renders the markdown; the JSON tab pretty-prints the
  // structured payload for developers. Default Preview because the markdown
  // is the human-facing view.
  const [contentView, setContentView] = useState<'preview' | 'json' | 'visual' | 'prototype'>('preview');
  const [stalenessFlags, setStalenessFlags] = useState<StalenessFlag[]>([]);
  const [resolvingFlagId, setResolvingFlagId] = useState<string | null>(null);
  // UXV.F2 (#1423) — F4 export gate. Effective toggle = per-project override
  // (GET /projects/:id) ?? tenant default (useMyTenant). FAIL-CLOSED: while the
  // project fetch is loading/errored, treat as OFF so the export action stays
  // hidden on an unknown flag state. Fetched ONLY for screen_spec artefacts.
  const [projectExportOverride, setProjectExportOverride] = useState<boolean | null>(null);
  const [projectExportFetchState, setProjectExportFetchState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  // DISC.A3 (#1475) — Phase 2.5 completion gate for the Claude Design export +
  // Copy Prompt actions. The project's artefacts (existing endpoint) supply the
  // five Phase-2.5 types' approval status, which ArtefactDetailContent's own
  // single-artefact scope does not carry. Fetched ONLY for screen_spec (the only
  // type that renders those actions). FAIL-CLOSED: the actions stay disabled
  // while this is idle / loading / errored.
  const [projectArtefacts, setProjectArtefacts] = useState<{ type: string; status: string }[]>([]);
  const [phase25FetchState, setPhase25FetchState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  // UXV.V2 (#1417) — screen list for the screen_spec Visual preview, parsed from
  // the latest version's { markdown, json } notes (no extra endpoint). Empty for
  // non-screen_spec artefacts or a spec set with no screens.
  const screenSpecScreens = useMemo<{ screenId: string; screenName: string }[]>(() => {
    if (artefact?.type !== 'screen_spec' || versions.length === 0 || !versions[0].notes) return [];
    try {
      const parsed = JSON.parse(versions[0].notes) as { json?: { specs?: { screenId: string; screenName: string }[] } };
      return parsed.json?.specs?.map((s) => ({ screenId: s.screenId, screenName: s.screenName })) ?? [];
    } catch { return []; }
  }, [artefact?.type, versions]);

  // Q3: default a screen_spec artefact with at least one screen to the Visual tab;
  // an empty set stays on Preview. UXV.V4 — guard on the initial 'preview' value so
  // this one-time default never yanks the user off a manually-chosen tab (e.g.
  // Prototype) when the screen-list length later changes (a new artefact version).
  useEffect(() => {
    if (artefact?.type === 'screen_spec' && screenSpecScreens.length > 0 && contentView === 'preview') setContentView('visual');
  }, [artefact?.type, screenSpecScreens.length, contentView]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setStalenessFlags([]);
    // UXV.F2 (#1423) — reset the export gate synchronously on artefact change so
    // a stale "loaded" override from the previous artefact can't briefly show the
    // export action for the new one before its own fetch runs (fail-closed across
    // transitions). The project-fetch effect below then re-evaluates.
    setProjectExportFetchState('idle');
    setProjectExportOverride(null);
    // DISC.A3 (#1475) — reset the Phase 2.5 gate too, so a stale "loaded" set
    // from the previous artefact can't briefly unlock the export action for the
    // new one before its own fetch runs (fail-closed across transitions).
    setPhase25FetchState('idle');
    setProjectArtefacts([]);
    Promise.all([
      apiFetch<ArtefactDetail>(`/artefacts/${artefactId}`),
      apiFetch<{ versions: VersionDetail[] }>(`/artefacts/${artefactId}/versions`),
    ])
      .then(([a, v]) => {
        if (cancelled) return;
        setArtefact(a);
        setVersions(v.versions);
        onMetadataLoaded?.({ title: a.title, type: a.type, status: a.status });
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load artefact'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    apiFetch<StalenessFlag[]>(`/artefacts/${artefactId}/staleness`).then(v => { if (!cancelled) setStalenessFlags(v); }).catch(err => { console.debug('[staleness fetch]', artefactId, err); });
    return () => { cancelled = true; };
  }, [artefactId, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // SL.D1 (#2951, 6406880252 item 3) — the project's phase, ONLY for a sprint_backlog:
  // its approval can be revoked in Phase 3 (Revoke plan approval, below).
  useEffect(() => {
    if (artefact?.type !== 'sprint_backlog') return;
    let cancelled = false;
    apiFetch<{ currentPhase?: string | null }>(`/projects/${artefact.projectId}`)
      .then(p => { if (!cancelled) setProjectPhase(p.currentPhase ?? null); })
      .catch(() => { if (!cancelled) setProjectPhase(null); });
    return () => { cancelled = true; };
  }, [artefact?.id, artefact?.type, artefact?.projectId]);

  const handleRevokePlanApproval = async () => {
    if (!artefact || revoking) return;
    setRevoking(true);
    setRevokeError('');
    try {
      await apiFetch(`/artefacts/${artefact.id}/revoke-plan-approval`, {
        method: 'POST',
        body: JSON.stringify({ reason: revokeReason.trim() }),
      });
      setRevokeOpen(false);
      setRevokeReason('');
      setReloadNonce(n => n + 1);
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Could not revoke the approval');
    } finally {
      setRevoking(false);
    }
  };

  // UXV.F2 (#1423) — fetch the project's Claude Design export override, ONLY for
  // screen_spec artefacts (no extra round-trip for other artefact types).
  // Combined with the tenant default from useMyTenant, drives the F4 gate below.
  useEffect(() => {
    if (artefact?.type !== 'screen_spec') return;
    let cancelled = false;
    setProjectExportFetchState('loading');
    apiFetch<{ claudeDesignExportEnabled: boolean | null }>(`/projects/${artefact.projectId}`)
      .then(p => { if (!cancelled) { setProjectExportOverride(p.claudeDesignExportEnabled ?? null); setProjectExportFetchState('loaded'); } })
      .catch(() => { if (!cancelled) setProjectExportFetchState('error'); });
    return () => { cancelled = true; };
    // DISC.A3 — re-run when the focused artefact id changes even if type +
    // projectId are unchanged (navigating between two screen_specs in the SAME
    // project), so the main effect's reset above doesn't strand this gate at
    // 'idle' (fail-closed: the export block would stay hidden). Pre-existing
    // UXV.F2 latent bug, hardened here because the Phase 2.5 gate renders inside
    // this export block and inherits the same path.
  }, [artefact?.id, artefact?.type, artefact?.projectId]);

  // DISC.A3 (#1475) — load the project's artefacts to derive Phase 2.5
  // completion, ONLY for screen_spec (the only type that renders the gated
  // actions). Mirrors the export-override fetch above. The list endpoint is
  // tenant-scoped and already called on the project dashboard. FAIL-CLOSED via
  // phase25FetchState: anything but a loaded fetch keeps the actions disabled.
  useEffect(() => {
    if (artefact?.type !== 'screen_spec') return;
    let cancelled = false;
    setPhase25FetchState('loading');
    apiFetch<{ type: string; status: string }[]>(`/artefacts?projectId=${artefact.projectId}`)
      .then(list => { if (!cancelled) { setProjectArtefacts(Array.isArray(list) ? list : []); setPhase25FetchState('loaded'); } })
      .catch(() => { if (!cancelled) setPhase25FetchState('error'); });
    return () => { cancelled = true; };
    // DISC.A3 — artefact?.id (see the export-override effect above) so this
    // re-runs when navigating between two screen_specs in the same project and
    // the gate isn't stranded at 'idle'.
  }, [artefact?.id, artefact?.type, artefact?.projectId]);

  // DISC.C1 (#1478) — load the caller's feature-acknowledgement map so the
  // round-trip tour can decide whether it still needs to show. ONLY for
  // screen_spec (the only type that renders the Export / Copy Prompt actions
  // that trigger it). FAIL-OPEN: on a fetch error treat as "nothing
  // acknowledged" — the tour is a harmless one-time nudge, so a transient error
  // should not permanently suppress it.
  useEffect(() => {
    if (artefact?.type !== 'screen_spec') return;
    let cancelled = false;
    apiFetch<{ feature_acknowledgements: Record<string, boolean> }>('/users/me/feature-acknowledgements')
      .then(r => { if (!cancelled) { setFeatureAck(r.feature_acknowledgements ?? {}); setFeatureAckLoaded(true); } })
      // Fail-OPEN only on a hard error: treat as "nothing acknowledged" and mark
      // loaded so a transient error doesn't permanently suppress the (harmless)
      // onboarding nudge for a genuinely new user.
      .catch(() => { if (!cancelled) { setFeatureAck({}); setFeatureAckLoaded(true); } });
    return () => { cancelled = true; };
  }, [artefact?.type]);

  // FAIL-CLOSED: false unless the project fetch has loaded AND the shared
  // resolver says on (project override wins, else tenant default).
  const effectiveExport =
    projectExportFetchState === 'loaded'
      ? getEffectiveClaudeDesignExportEnabled(projectExportOverride, myTenant?.claudeDesignExportEnabled)
      : false;

  // DISC.A3 (#1475) — all five Phase 2.5 artefacts must be `approved` before the
  // Claude Design export / Copy Prompt actions unlock. FAIL-CLOSED: false unless
  // the project-artefacts fetch has loaded.
  const phase25Complete =
    phase25FetchState === 'loaded' &&
    PHASE_25_TYPES.every(t => projectArtefacts.some(a => a.type === t && a.status === 'approved'));

  // Disabled-reason tooltip for the gated actions — distinguishes loading /
  // error / incomplete so the user knows WHY the action is unavailable. Null
  // once Phase 2.5 is complete (the buttons fall back to their own tooltips).
  const phase25GateTooltip =
    phase25FetchState === 'loading' ? 'Checking Phase 2.5 status…'
    : phase25FetchState === 'error' ? 'Unable to verify Phase 2.5 status'
    : !phase25Complete ? 'Complete Phase 2.5 first — all five UX Design artefacts must be approved before exporting'
    : null;

  // DISC.C1 (#1478) — the round-trip tour shows when the user has triggered it
  // (clicked Export / Copy Prompt), the ack read has SETTLED, and it is not
  // already acknowledged. Gating on featureAckLoaded means a click that lands
  // before the read resolves waits for the answer rather than flashing the tour
  // at an already-dismissed user (R2).
  const tourAcknowledged = featureAck?.[CLAUDE_DESIGN_TOUR_FEATURE] === true;
  const tourOpen = tourTriggered && featureAckLoaded && !tourAcknowledged;

  // Fire the tour alongside the download/copy — non-blocking. No-op once
  // acknowledged so a returning user is never nagged.
  const maybeTriggerTour = () => {
    if (!tourAcknowledged) setTourTriggered(true);
  };

  // The tour closes itself and reports whether it persisted the acknowledgement
  // (true unless the user unchecked "Don't show again"). Update local state
  // optimistically so a "Don't show again" dismiss sticks for the rest of the
  // session WITHOUT a re-fetch round-trip / race against the background POST.
  const handleTourDismiss = (persisted: boolean) => {
    setTourTriggered(false);
    if (persisted) setFeatureAck(prev => ({ ...(prev ?? {}), [CLAUDE_DESIGN_TOUR_FEATURE]: true }));
  };

  // Sprint 11 UX.F2 — "Open in Claude Design" handler.
  // Downloads the export bundle as a ZIP. Direct deep link to Claude Design
  // is future work; for now we ship the ZIP + a clipboard-copy fallback.
  const handleClaudeDesignExport = async () => {
    if (!artefact) return;
    // DISC.C1 — kick off the tour first so it appears alongside the download
    // that starts immediately below (the modal does not block it).
    maybeTriggerTour();
    setClaudeExporting(true);
    setExportError('');
    try {
      // #3704 — the same call the chat's Claude Design card makes (lib/claudeDesignExport.ts).
      await downloadClaudeDesignZip(artefact.projectId, artefact.title);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Claude Design export failed');
    } finally {
      setClaudeExporting(false);
    }
  };

  // Fallback per issue #580 AC: copy-to-clipboard + manual paste instruction
  // when deep link unavailable. We expose this as a secondary action so users
  // can paste PROMPT.md directly into a Claude Design session.
  const handleCopyClaudeDesignPrompt = async () => {
    if (!artefact) return;
    // DISC.C1 — same non-blocking tour trigger as the ZIP export.
    maybeTriggerTour();
    try {
      await navigator.clipboard.writeText(await fetchClaudeDesignPrompt(artefact.projectId));
      setClaudeCopied(true);
      setTimeout(() => setClaudeCopied(false), 2000);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Copy failed');
    }
  };

  const handleResolveStaleness = async (flagId: string, status: 'resolved' | 'dismissed' = 'resolved') => {
    setResolvingFlagId(flagId);
    try {
      await apiFetch(`/staleness/${flagId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setStalenessFlags(prev => prev.filter(f => f.id !== flagId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update staleness flag');
    } finally { setResolvingFlagId(null); }
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse space-y-4" style={{ background: 'var(--bg)' }}>
        <div className="h-6 rounded w-48" style={{ background: 'var(--surface-alt)' }} />
        <div className="h-4 rounded w-96" style={{ background: 'var(--surface-alt)' }} />
        <div className="h-64 rounded" style={{ background: 'var(--surface-alt)' }} />
      </div>
    );
  }

  if (error || !artefact) {
    return (
      <div className="p-6">
        <p className="text-red-600 mb-2">{error || 'Artefact not found'}</p>
      </div>
    );
  }

  const isImportedStatus = artefact.status === 'imported' || artefact.status === 'needs_review';

  const handleApprove = async () => {
    setApproving(true);
    setApproveError('');
    try {
      if (isImportedStatus) {
        // BF.B1: approve imported artefact via dedicated endpoint
        await apiFetch(`/artefacts/${artefact.id}/approve-import`, { method: 'POST' });
      } else {
        await apiFetch(`/artefacts/${artefact.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'approved' }),
        });
      }
      const updated = { ...artefact, status: 'approved' };
      setArtefact(updated);
      onMetadataLoaded?.({ title: updated.title, type: updated.type, status: 'approved' });
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleExport = async (format?: string) => {
    const fmt = format ?? '';
    setExporting(true);
    setExportingFormat(fmt || 'docx');
    try {
      const token = localStorage.getItem('blueprint_token');
      const url = `${API_BASE}/artefacts/${artefact.id}/export${fmt ? `?format=${fmt}` : ''}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setExportError(body?.message ?? 'Export failed');
        setExportNotice(null);
        return;
      }
      setExportError('');
      const diagramsFailed = Number(res.headers.get('X-Docx-Diagrams-Failed') ?? 0);
      const diagramsSkipped = Number(res.headers.get('X-Docx-Diagrams-Skipped') ?? 0);
      // Delta round 3 (D7b-4): busy refusals may render on a re-export; the rest are over the cap.
      const diagramsBusy = Number(res.headers.get('X-Docx-Diagrams-Busy') ?? 0);
      const diagramsCapped = diagramsSkipped - diagramsBusy;
      const sentences: string[] = [];
      if (diagramsFailed > 0) sentences.push(`${diagramsFailed} diagram${diagramsFailed === 1 ? '' : 's'} could not be rendered; the document shows ${diagramsFailed === 1 ? 'its' : 'their'} source instead. Try exporting again later.`);
      if (diagramsCapped > 0) sentences.push(`${diagramsCapped} diagram${diagramsCapped === 1 ? ' was' : 's were'} not rendered because the document has more than 50 distinct diagrams; the document shows ${diagramsCapped === 1 ? 'its' : 'their'} source instead.`);
      // Delta round 3 (D8) / round 4 (D9b): the cover lookup failed; the cover was built without the approver.
      const coverDegraded = res.headers.get('X-Docx-Cover-Degraded') === '1';
      if (diagramsBusy > 0) sentences.push(`${diagramsBusy} diagram${diagramsBusy === 1 ? ' was' : 's were'} not rendered because the diagram service was busy; the document shows ${diagramsBusy === 1 ? 'its' : 'their'} source instead. Exporting again may help.`);
      if (coverDegraded) sentences.push('The cover page could not include the approver; export again to retry.');
      setExportNotice(sentences.length > 0 ? { artefactId: artefact.id, text: sentences.join(' ') } : null);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fmt === 'json' ? `${artefact.title}.json` : fmt === 'csv' ? `${artefact.title}.csv` : `${artefact.title}.docx`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setExporting(false);
      setExportingFormat(null);
    }
  };

  let prdMarkdown: string | null = null;
  if (MARKDOWN_TYPES.includes(artefact.type) && versions.length > 0) {
    const latest = versions[0];
    if (latest.notes) {
      try {
        const parsed = JSON.parse(latest.notes) as { markdown?: string };
        if (typeof parsed.markdown === 'string') prdMarkdown = parsed.markdown;
      } catch {
        // fall through to generic viewer
      }
    }
  }

  // #2057 item 1a — surfaces the generation-time flag prdGenerator.ts stores
  // alongside the PRD markdown: does this version have a conforming
  // "### 9.2 Open Questions" heading? Independent of the confidence-score
  // banner above (which only renders when FR-level [confidence: N] tags
  // exist) -- a PRD with zero FR tags still needs this warning visible.
  let openQuestionsSectionMissing = false;
  if (artefact.type === 'prd' && versions.length > 0 && versions[0].notes) {
    try {
      const parsed = JSON.parse(versions[0].notes) as { openQuestionsSectionMissing?: boolean };
      openQuestionsSectionMissing = parsed.openQuestionsSectionMissing === true;
    } catch { /* fall through */ }
  }

  let backlogJson: SprintBacklogJson | null = null;
  if (artefact.type === 'sprint_backlog' && versions.length > 0 && versions[0].notes) {
    backlogJson = isSprintBacklog(versions[0].notes);
  }

  let analysisReport: AnalysisReport | null = null;
  if (artefact.type === 'analysis_report' && versions.length > 0) {
    const latest = versions[0];
    if (latest.notes) {
      try {
        const parsed: unknown = JSON.parse(latest.notes);
        if (isAnalysisReport(parsed)) analysisReport = parsed;
      } catch {
        // fall through to generic viewer
      }
    }
  }

  let barReport: BrownfieldAssessment | null = null;
  if (artefact.type === 'brownfield_assessment' && versions.length > 0) {
    const latest = versions[0];
    if (latest.notes) {
      try {
        const parsed: unknown = JSON.parse(latest.notes);
        if (isBrownfieldAssessment(parsed)) barReport = parsed;
      } catch {
        // fall through to generic viewer
      }
    }
  }

  let qaReport: QAReport | null = null;
  if (artefact.type === 'qa_report' && versions.length > 0) {
    const latest = versions[0];
    if (latest.notes) {
      try {
        const parsed: unknown = JSON.parse(latest.notes);
        if (isQAReport(parsed)) qaReport = parsed;
      } catch {
        // fall through to generic viewer
      }
    }
  }

  // SEC.I4 C1 (Option D) — render the Code Review Report card on the artefact
  // detail page. This is the surface C1 revealed was missing: before C1,
  // accelerate auto-resolved-and-HID issues, so there was no place to review and
  // acknowledge an approved report's issues. The card here receives the artefact's
  // approval status, so its last-ack affordance can state the merge consequence
  // BEFORE the human clicks (the merge must not be silent on the final ack).
  let codeReviewReport: CodeReviewReport | null = null;
  if (artefact.type === 'code_review_report' && versions.length > 0 && versions[0].notes) {
    try {
      const parsed = JSON.parse(versions[0].notes) as Partial<CodeReviewReport>;
      // Full shape check — the card unconditionally dereferences these (e.g.
      // adrCompliance.length, summary). A malformed/legacy note that passes a
      // looser check would throw in render. The RenderErrorBoundary below is the
      // backstop; this guard avoids hitting it on known-incomplete shapes.
      if (parsed && typeof parsed === 'object'
          && typeof parsed.summary === 'string'
          && typeof parsed.verdict === 'string'
          && parsed.summaryStats != null && typeof parsed.summaryStats === 'object'
          && Array.isArray(parsed.issues)
          && Array.isArray(parsed.adrCompliance)) {
        codeReviewReport = parsed as CodeReviewReport;
      }
    } catch {
      // fall through to generic viewer
    }
  }

  const approveLabel = isImportedStatus
    ? 'Approve Import'
    : artefact.type === 'user_stories' ? 'Approve Stories'
    : artefact.type === 'sad' ? 'Approve SAD'
    : artefact.type === 'adr' ? 'Approve ADRs'
    : artefact.type === 'sprint_plan' ? 'Approve Sprint Plan'
    : artefact.type === 'sprint_backlog' ? 'Approve Backlog'
    : artefact.type === 'qa_report' ? 'Approve QA Report'
    : artefact.type === 'code_review_report' ? 'Approve Review'
    : artefact.type === 'user_journey_map' ? 'Approve Journey Map'
    : artefact.type === 'screen_inventory' ? 'Approve Screen Inventory'
    : artefact.type === 'screen_spec' ? 'Approve Screen Specs'
    : artefact.type === 'component_catalogue' ? 'Approve Catalogue'
    : artefact.type === 'design_tokens' ? 'Approve Design Tokens'
    : artefact.type === 'claude_design_bundle' ? 'Approve Design Bundle'
    : 'Approve PRD';

  const statusBadgeStyle = (status: string): React.CSSProperties => {
    if (status === 'approved') return { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' };
    if (status === 'handed_off') return { background: 'rgba(124,58,237,0.10)', color: 'var(--primary)' };
    if (status === 'imported') return { background: 'rgba(59,130,246,0.12)', color: '#3B82F6' };
    if (status === 'needs_review') return { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' };
    return { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' };
  };

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <div className="px-6 py-4 flex-shrink-0" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <Link
            to={`/projects/${artefact.projectId}`}
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--primary)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
          >
            ← Back to Project
          </Link>
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)' }} className="truncate">{artefact.title}</h1>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, background: 'var(--surface-alt)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 9999 }} className="whitespace-nowrap flex-shrink-0">
              {artefact.type.replace(/_/g, ' ')}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }} className="whitespace-nowrap flex-shrink-0">v{artefact.currentVersion}</span>
            {artefact.status && (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 9999, ...statusBadgeStyle(artefact.status) }} className="whitespace-nowrap flex-shrink-0">
                {artefact.status}
              </span>
            )}
            {/* DRV-5b(ii) (#2883) — Drive filing badge, viewer header only (D-3).
                Keyed on the status so an approval here re-reads the filing. */}
            <FilingBadge
              key={`${artefact.id}:${artefact.status}`}
              artefactId={artefact.id}
              isAdmin={isAdminRole(myTenant?.role)}
              latestApproved={artefact.status === 'approved' || artefact.status === 'handed_off'}
            />
            {/* AUDITUI.A3 (#2588) — provenance line, opening the history drawer.
                Artefact scope only; the rollout to other governed objects is A4. */}
            <ArtefactProvenanceLine artefactId={artefact.id} />
          </div>
          {/* #3873 — min-w-0, not flex-shrink-0: the group wraps inside the panel instead of stretching past it. */}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {artefact.status === 'handed_off' ? (
              <>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(245,158,11,0.12)', color: 'var(--warning)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)' }}>
                  🔒 Locked — handed off
                </span>
                {versions.length > 0 && versions[0].notes && (
                  <button
                    onClick={() => void handleExport()}
                    disabled={exporting}
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                      padding: '6px 12px', background: 'transparent',
                      border: '1px solid rgba(124,58,237,0.3)', borderRadius: 'var(--radius-sm)',
                      color: 'var(--primary)', cursor: exporting ? 'not-allowed' : 'pointer',
                      opacity: exporting ? 0.5 : 1,
                    }}
                    onMouseEnter={e => { if (!exporting) { e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; e.currentTarget.style.borderColor = 'var(--primary)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; }}
                  >
                    {exporting ? 'Exporting…' : 'Export .docx'}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/projects/${artefact.projectId}/amendments`)}
                  style={{
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                    padding: '6px 12px', background: 'transparent',
                    border: '1px solid rgba(124,58,237,0.3)', borderRadius: 'var(--radius-sm)',
                    color: 'var(--primary)', cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; }}
                >
                  📝 Request Amendment
                </button>
              </>
            ) : (
              <>
                {/* #3873 — the primary action comes FIRST, so it is never the one pushed out of view. */}
                {(APPROVABLE_DRAFT_TYPES.includes(artefact.type) || isImportedStatus) && (artefact.status === 'draft' || isImportedStatus) && (
                  <>
                    {approveError && <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', overflowWrap: 'anywhere' }}>{approveError}</p>}
                    <button
                      onClick={() => void handleApprove()}
                      disabled={approving}
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 13,
                        fontWeight: 600,
                        padding: '6px 14px',
                        color: 'white',
                        background: 'var(--primary)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: approving ? 'not-allowed' : 'pointer',
                        opacity: approving ? 0.7 : 1,
                        transition: 'box-shadow 150ms',
                      }}
                      onMouseEnter={e => { if (!approving) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {approving ? 'Approving…' : approveLabel}
                    </button>
                  </>
                )}
                {artefact.type === 'sprint_backlog' && (
                  <>
                    {(['json', 'csv', ''] as const).map(fmt => {
                      const label = fmt === 'json' ? 'Export JSON' : fmt === 'csv' ? 'Export CSV' : 'Export .docx';
                      const busy = exporting && exportingFormat === (fmt || 'docx');
                      return (
                        <button
                          key={fmt || 'docx'}
                          onClick={() => void handleExport(fmt || undefined)}
                          disabled={exporting}
                          style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '6px 14px', color: 'var(--primary)', background: 'transparent', border: '1px solid rgba(124,58,237,0.30)', borderRadius: 'var(--radius-sm)', cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.5 : 1, transition: 'border-color 150ms, background 150ms' }}
                          onMouseEnter={e => { if (!exporting) { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.30)'; e.currentTarget.style.background = 'transparent'; }}
                        >
                          {busy ? 'Exporting…' : label}
                        </button>
                      );
                    })}
                  </>
                )}
                {artefact.type !== 'sprint_backlog' && versions.length > 0 && versions[0].notes && (
                  <button
                    onClick={() => void handleExport()}
                    disabled={exporting}
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '6px 14px',
                      color: 'var(--primary)',
                      background: 'transparent',
                      border: '1px solid rgba(124,58,237,0.30)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: exporting ? 'not-allowed' : 'pointer',
                      opacity: exporting ? 0.5 : 1,
                      transition: 'border-color 150ms, background 150ms',
                    }}
                    onMouseEnter={e => { if (!exporting) { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.30)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {exporting ? 'Exporting…' : 'Export .docx'}
                  </button>
                )}
                {/* Sprint 11 UX.F2 — export action on screen_spec artefacts.
                    UXV.F2 (#1423): gated on the effective F4 toggle (fail-closed —
                    hidden while the project override fetch is loading/errored). */}
                {artefact.type === 'screen_spec' && versions.length > 0 && versions[0].notes && effectiveExport && (
                  <>
                    <button
                      data-testid="claude-design-export-action"
                      onClick={() => void handleClaudeDesignExport()}
                      disabled={claudeExporting || !phase25Complete}
                      title={phase25GateTooltip ?? 'Download a Claude Design-compatible export bundle (PROMPT.md + tokens.json + screen-context.md) as a ZIP'}
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        color: 'white',
                        background: '#EC4899',
                        border: '1px solid #EC4899',
                        borderRadius: 'var(--radius-sm)',
                        cursor: (claudeExporting || !phase25Complete) ? 'not-allowed' : 'pointer',
                        opacity: (claudeExporting || !phase25Complete) ? 0.5 : 1,
                        transition: 'box-shadow 150ms',
                      }}
                      onMouseEnter={e => { if (!claudeExporting && phase25Complete) e.currentTarget.style.boxShadow = '0 0 0 3px rgba(236,72,153,0.25)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {claudeExporting ? 'Exporting…' : 'Export to Claude Design'}
                    </button>
                    <button
                      data-testid="claude-design-copy-prompt-action"
                      onClick={() => void handleCopyClaudeDesignPrompt()}
                      disabled={!phase25Complete}
                      title={phase25GateTooltip ?? "Copy the seed prompt to clipboard as a fallback when the ZIP download isn't suitable"}
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        color: '#EC4899',
                        background: 'transparent',
                        border: '1px solid #EC4899',
                        borderRadius: 'var(--radius-sm)',
                        cursor: phase25Complete ? 'pointer' : 'not-allowed',
                        opacity: phase25Complete ? 1 : 0.5,
                      }}
                    >
                      {claudeCopied ? '✓ Copied!' : 'Copy Prompt'}
                    </button>
                    {/* UXV.F2 (#1423) AC5 — label the manual step; the action is a
                        download-and-paste, not a direct integration. */}
                    <span
                      data-testid="claude-design-export-manual-note"
                      style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center' }}
                    >
                      Downloads a bundle — open Claude Design and paste the PROMPT.md.
                      After designing, export the handoff bundle from Claude Design and
                      import it via the Claude Design bundle card on your project dashboard.
                    </span>
                    {/* DISC.C1 (#1478) — guided round-trip tour, opened alongside the
                        download the first time the user exports / copies. Mounted
                        ONLY while open so each open is a fresh tour (R2). */}
                    {tourOpen && <ClaudeDesignTour open onDismiss={handleTourDismiss} />}
                  </>
                )}
                {/* UXV.F3 (#1424) — "Import token refinements" on design_tokens artefacts.
                    NOT gated by the F4 export toggle — the round-trip is always available. */}
                {artefact.type === 'design_tokens' && versions.length > 0 && (
                  <button
                    onClick={() => setShowReimport(true)}
                    title="Import refined design tokens from a Claude Design export and review the changes before committing a new version"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '6px 14px',
                      color: '#EC4899',
                      background: 'transparent',
                      border: '1px solid #EC4899',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    Import token refinements
                  </button>
                )}
                {showReimport && artefact.type === 'design_tokens' && (
                  <TokenReimportModal
                    projectId={artefact.projectId}
                    onClose={() => setShowReimport(false)}
                    onApplied={() => { setShowReimport(false); setReloadNonce(n => n + 1); }}
                  />
                )}
                {/* #3873 (R2) — overflowWrap 'anywhere': a long unbroken token in a message wraps instead of widening the group. */}
                {exportError && <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', overflowWrap: 'anywhere' }}>{exportError}</p>}
                {exportNotice?.artefactId === artefact.id && <p role="status" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--warning)' }}>{exportNotice.text}</p>}
                {/* SL.D1 (#2951, 6406880252 item 3): a sprint plan approved in Phase 3 — any approved
                    version counts for Q2 — can be revoked by its approver or the PM (the server checks). */}
                {artefact.type === 'sprint_backlog' && projectPhase === 'phase_3_sprint_planning'
                  && (artefact.status === 'approved' || versions.some(v => v.status === 'approved')) && (
                  <>
                    {!revokeOpen ? (
                      <button
                        type="button"
                        onClick={() => setRevokeOpen(true)}
                        style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '6px 14px', color: 'var(--error)', background: 'transparent', border: '1px solid var(--error)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                      >
                        Revoke plan approval
                      </button>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <input
                          aria-label="Reason for revoking the plan approval"
                          placeholder="Why (at least 10 characters)"
                          value={revokeReason}
                          onChange={e => setRevokeReason(e.target.value)}
                          style={{ fontFamily: 'var(--font-body)', fontSize: 13, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', minWidth: 220 }}
                        />
                        <button
                          type="button"
                          onClick={() => void handleRevokePlanApproval()}
                          disabled={revoking || revokeReason.trim().length < 10}
                          style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '6px 14px', color: 'white', background: 'var(--error)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: revoking || revokeReason.trim().length < 10 ? 'not-allowed' : 'pointer', opacity: revoking || revokeReason.trim().length < 10 ? 0.6 : 1 }}
                        >
                          {revoking ? 'Revoking…' : 'Revoke approval'}
                        </button>
                        <button type="button" onClick={() => { setRevokeOpen(false); setRevokeError(''); }}
                          style={{ fontFamily: 'var(--font-body)', fontSize: 12, background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </span>
                    )}
                    {revokeError && <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', overflowWrap: 'anywhere' }}>{revokeError}</p>}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Staleness warning */}
      {stalenessFlags.length > 0 && (
        <div style={{ margin: '12px 24px 0', padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--warning)', marginBottom: 6 }}>⚠ This artefact may be outdated</p>
          {stalenessFlags.map(flag => (
            <div key={flag.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>{flag.reason}</p>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => void handleResolveStaleness(flag.id, 'resolved')}
                  disabled={resolvingFlagId === flag.id}
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '4px 10px', background: 'transparent', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 'var(--radius-sm)', color: 'var(--warning)', cursor: resolvingFlagId === flag.id ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: resolvingFlagId === flag.id ? 0.6 : 1 }}
                >
                  {resolvingFlagId === flag.id ? 'Resolving…' : 'Mark Reviewed'}
                </button>
                <button
                  onClick={() => void handleResolveStaleness(flag.id, 'dismissed')}
                  disabled={resolvingFlagId === flag.id}
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: resolvingFlagId === flag.id ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: resolvingFlagId === flag.id ? 0.6 : 1 }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="px-6 py-6 flex-1 overflow-auto">
        {/* #3851 — the latest version's JSON was rebuilt by BluePrint, not written by the agent: say so before approval. */}
        {(() => {
          const source = versions.length > 0
            ? structureRebuiltSourceLabel((versions[0].confidenceMetadata as { structureRebuiltFrom?: unknown } | null)?.structureRebuiltFrom)
            : null;
          if (!source) return null;
          return (
            <div
              role="note"
              data-testid="artefact-structure-rebuilt-mark"
              style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: 12, color: '#b45309' }}
            >
              {`⚠ The latest version's structured data (the JSON tab) was rebuilt by BluePrint from the ${source}, not taken from the agent's reply. The agent's reply had no usable JSON block. Check it before approving.`}
            </div>
          );
        })()}
        {artefact.type === 'prototype' ? (
          // DEF-9 (AC6.4) — before this branch, a `prototype` artefact fell
          // through to the generic fallback below and dumped its raw
          // `{ html, fidelity, screenIds }` JSON envelope. PrototypeViewer
          // wires the existing, correctly-versioned GET .../prototype +
          // annotation routes that previously had NO frontend caller at all.
          <PrototypeViewer artefactId={artefact.id} versions={versions} />
        ) : barReport ? (
          <RenderErrorBoundary fallback={barRenderFallback}>
            <BrownfieldAssessmentView report={barReport} />
          </RenderErrorBoundary>
        ) : codeReviewReport ? (
          <RenderErrorBoundary fallback={crRenderFallback}>
            <CodeReviewReportCard
              report={codeReviewReport}
              artefactId={artefact.id}
              artefactStatus={artefact.status}
            />
          </RenderErrorBoundary>
        ) : qaReport ? (
          <QAReportView report={qaReport} />
        ) : backlogJson ? (
          <SprintBacklogView backlog={backlogJson} />
        ) : prdMarkdown ? (
          <>
            {artefact.type === 'prd' && versions.length > 0 && versions[0].confidenceScore && (() => {
              const score = parseFloat(versions[0].confidenceScore!);
              const meta = versions[0].confidenceMetadata;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Overall confidence:</span>
                  <ConfidenceBar score={score} />
                  {score < 0.7 && meta && meta.lowConfidenceCount > 0 && (
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#f59e0b' }}>
                      {meta.lowConfidenceCount} requirement(s) need PM review
                      {meta.flaggedForReview.length > 0 && ` (${meta.flaggedForReview.join(', ')})`}
                    </span>
                  )}
                </div>
              );
            })()}
            {openQuestionsSectionMissing && (
              <div
                data-testid="open-questions-section-missing-banner"
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-md)' }}
              >
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#f59e0b' }}>
                  ⚠ This PRD is missing its 9.2 Open Questions section — a generation defect, not an expected state. The prompt template requires every PRD to include this section (writing None identified when there are no questions), so its absence means the agent did not follow its own instructions. Any open questions written elsewhere in the document are invisible to tracking and the Phase 1 exit gate.
                </span>
              </div>
            )}
            {/* #2369 — the only direct PRD-edit affordance in this view (was
                chat-only before this); FR-level structured edits only, no
                raw markdown editing surface.
                #2415 — hidden once handed_off, same lock-parity rule the
                header button set above already enforces (line ~1029): the
                service's own 400 ("locked — handed off") is a second line
                of defense, not the primary UX — a control that is live and
                then rejects on Save reads as broken, not as locked. */}
            {artefact.type === 'prd' && versions.length > 0 && artefact.status !== 'handed_off' && (
              <PrdFrEditor
                artefactId={artefact.id}
                markdown={prdMarkdown}
                baseVersionId={versions[0].id}
                onSaved={() => setReloadNonce((n) => n + 1)}
              />
            )}
            <div
              className="prose prose-sm max-w-none prose-headings:font-display prose-h1:text-2xl prose-h2:text-lg prose-h2:border-b prose-h2:pb-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 32 }}
            >
              {/* Sprint 12.5 ACC.B6 — per-section feedback wrapper.
                  Gated on the tenant feature flag. When the flag is
                  off, falls through to the original MarkdownRenderer
                  call so artefact rendering is byte-identical to
                  pre-A6 for tenants who haven't opted in. */}
              {myTenant?.qualityFeedbackEnabled ? (
                <ArtefactSectionFeedback artefactId={artefact.id} body={prdMarkdown} />
              ) : (
                <MarkdownRenderer>
                  {prdMarkdown}
                </MarkdownRenderer>
              )}
            </div>
          </>
        ) : analysisReport ? (
          <>
            <AnalysisReportView report={analysisReport} />
            {analysisReport.sourceArtefactIds && analysisReport.sourceArtefactIds.length > 0 && (
              <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                  Analysed Artefacts ({analysisReport.sourceArtefactIds.length})
                </h3>
                <ul className="space-y-1">
                  {analysisReport.sourceArtefactIds.map(aid => (
                    <li key={aid}>
                      <Link
                        to={`/artefacts?tabs=${aid}&active=${aid}`}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        {aid}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg p-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Content</h2>
            {versions.length > 0 && versions[0].notes ? (
              (() => {
                const rawNotes = versions[0].notes!;
                try {
                  const parsed = JSON.parse(rawNotes) as Record<string, unknown>;
                  if (typeof parsed.text === 'string') {
                    // Imported artefact content — extract readable text
                    return (
                      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
                        <MarkdownRenderer
                          className="prose prose-sm max-w-none prose-headings:font-display prose-h1:text-2xl prose-h2:text-lg prose-h2:border-b prose-h2:pb-1"
                          swatchHexCells={artefact?.type === 'design_tokens'}
                        >
                          {parsed.text}
                        </MarkdownRenderer>
                      </div>
                    );
                  }
                  // Phase 2.5 UX artefacts (user_journey_map, screen_inventory,
                  // screen_spec, component_catalogue, design_tokens) store
                  // notes as `{ markdown, json }`. The Preview tab renders the
                  // markdown — what the user actually wrote / the agent
                  // produced — and the JSON tab pretty-prints the structured
                  // payload for developers debugging downstream consumers.
                  // Without this branch the page used to show raw JSON to
                  // every user (the bug this PR fixes).
                  if (typeof parsed.markdown === 'string') {
                    const hasJson = parsed.json !== undefined;
                    return (
                      <>
                        {hasJson && (
                          <div data-testid="content-view-toggle" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            {screenSpecScreens.length > 0 && (
                              <button
                                type="button"
                                data-testid="content-view-visual"
                                onClick={() => setContentView('visual')}
                                aria-pressed={contentView === 'visual'}
                                style={{
                                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                  padding: '4px 12px', borderRadius: 'var(--radius-sm)',
                                  background: contentView === 'visual' ? 'var(--primary)' : 'transparent',
                                  color: contentView === 'visual' ? 'white' : 'var(--text-secondary)',
                                  border: contentView === 'visual' ? 'none' : '1px solid var(--border)',
                                  cursor: 'pointer',
                                }}
                              >
                                Visual
                              </button>
                            )}
                            {screenSpecScreens.length > 0 && (
                              <button
                                type="button"
                                data-testid="content-view-prototype"
                                onClick={() => setContentView('prototype')}
                                aria-pressed={contentView === 'prototype'}
                                style={{
                                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                  padding: '4px 12px', borderRadius: 'var(--radius-sm)',
                                  background: contentView === 'prototype' ? 'var(--primary)' : 'transparent',
                                  color: contentView === 'prototype' ? 'white' : 'var(--text-secondary)',
                                  border: contentView === 'prototype' ? 'none' : '1px solid var(--border)',
                                  cursor: 'pointer',
                                }}
                              >
                                Prototype
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid="content-view-preview"
                              onClick={() => setContentView('preview')}
                              aria-pressed={contentView === 'preview'}
                              style={{
                                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                padding: '4px 12px', borderRadius: 'var(--radius-sm)',
                                background: contentView === 'preview' ? 'var(--primary)' : 'transparent',
                                color: contentView === 'preview' ? 'white' : 'var(--text-secondary)',
                                border: contentView === 'preview' ? 'none' : '1px solid var(--border)',
                                cursor: 'pointer',
                              }}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              data-testid="content-view-json"
                              onClick={() => setContentView('json')}
                              aria-pressed={contentView === 'json'}
                              style={{
                                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                padding: '4px 12px', borderRadius: 'var(--radius-sm)',
                                background: contentView === 'json' ? 'var(--primary)' : 'transparent',
                                color: contentView === 'json' ? 'white' : 'var(--text-secondary)',
                                border: contentView === 'json' ? 'none' : '1px solid var(--border)',
                                cursor: 'pointer',
                              }}
                            >
                              JSON
                            </button>
                          </div>
                        )}
                        {contentView === 'prototype' && screenSpecScreens.length > 0 ? (
                          <ScreenPreviewCard key={`proto-${artefactId}`} artefactId={artefactId} screens={screenSpecScreens} flow />
                        ) : contentView === 'visual' && screenSpecScreens.length > 0 ? (
                          <ScreenPreviewCard key={artefactId} artefactId={artefactId} screens={screenSpecScreens} />
                        ) : contentView === 'preview' || !hasJson ? (
                          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
                            <MarkdownRenderer
                              className="prose prose-sm max-w-none prose-headings:font-display prose-h1:text-2xl prose-h2:text-lg prose-h2:border-b prose-h2:pb-1"
                              swatchHexCells={artefact?.type === 'design_tokens'}
                            >
                              {parsed.markdown}
                            </MarkdownRenderer>
                          </div>
                        ) : (
                          <pre data-testid="content-json-pane" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)', background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', padding: 16, border: '1px solid var(--border)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(parsed.json, null, 2)}
                          </pre>
                        )}
                      </>
                    );
                  }
                  // Valid JSON without a text or markdown field — pretty-print
                  return (
                    <pre style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)', background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', padding: 16, border: '1px solid var(--border)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(parsed, null, 2)}
                    </pre>
                  );
                } catch {
                  // Plain text / markdown — render with MarkdownRenderer
                  return (
                    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
                      <MarkdownRenderer
                        className="prose prose-sm max-w-none prose-headings:font-display prose-h1:text-2xl prose-h2:text-lg prose-h2:border-b prose-h2:pb-1"
                        swatchHexCells={artefact?.type === 'design_tokens'}
                      >
                        {rawNotes}
                      </MarkdownRenderer>
                    </div>
                  );
                }
              })()
            ) : (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No content available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
